import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, FormControl, InputLabel, Select, MenuItem, Pagination,
} from '@mui/material';
import { gifticonOrderAPI } from '../services/api';
import { formatKstDateTime } from '../utils/datetime';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

const STATUS_OPTIONS = ['pending', 'issued', 'failed', 'cancelled', 'used', 'expired'];
const STATUS_LABEL: Record<string, string> = {
  pending: '발급대기', issued: '발급완료', failed: '발급실패', cancelled: '취소', used: '사용완료', expired: '만료',
};
const STATUS_COLOR: Record<string, any> = {
  pending: 'warning', issued: 'success', failed: 'error', cancelled: 'default', used: 'info', expired: 'default',
};

const GifticonOrders: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data } = useQuery({
    queryKey: ['gifticon-orders', status, page],
    queryFn: async () => (await gifticonOrderAPI.getAll({ status: status || undefined, page, limit })).data,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, s }: { id: number; s: string }) => gifticonOrderAPI.updateStatus(id, s),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gifticon-orders'] }),
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>기프티콘 주문 내역</Typography>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>상태 필터</InputLabel>
          <Select value={status} label="상태 필터" onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <MenuItem value="">전체</MenuItem>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell><TableCell>유저</TableCell><TableCell>상품</TableCell>
              <TableCell align="right">포인트</TableCell><TableCell>PIN</TableCell><TableCell>전화</TableCell>
              <TableCell>일시</TableCell><TableCell width={140}>상태</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items?.map((o) => (
              <TableRow key={o.id}>
                <TableCell>{o.id}</TableCell>
                <TableCell>#{o.userId}</TableCell>
                <TableCell>{o.brandName ? `${o.brandName} ` : ''}{o.goodsName}</TableCell>
                <TableCell align="right">{fmt(o.pointPrice)}</TableCell>
                <TableCell>{o.pinNo || '-'}</TableCell>
                <TableCell>{o.phone || '-'}</TableCell>
                <TableCell>{formatKstDateTime(o.createdAt)}</TableCell>
                <TableCell>
                  <Select
                    size="small" value={o.status}
                    onChange={(e) => statusMutation.mutate({ id: o.id, s: e.target.value })}
                    renderValue={(v) => <Chip label={STATUS_LABEL[v] || v} color={STATUS_COLOR[v] || 'default'} size="small" />}
                  >
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {!data?.items?.length && <TableRow><TableCell colSpan={8} align="center">주문 내역이 없습니다.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};

export default GifticonOrders;
